/**
 * Property videos — the Videos group (`templates/videos/_videos_group.html`).
 *
 * Upload: "Upload video" opens a hidden file input.  The file is checked here for size and a
 * video/* type before anything is sent, then POSTed by XHR to `[data-video-upload-url]` with a
 * percent progress bar (`.file-uploader-progress-bar` width) in a placeholder tile cloned from `<template data-video-upload-tile>`.
 * The response is the server-rendered processing card (`videos/_video_card.html`), which polls
 * itself through htmx until it is ready or failed.  nginx answers an oversize body with its own
 * 413 page, so a 413 gets its own message.
 *
 * Player: a ready tile carries a `<template data-video-player>` holding SeeMe lightbox markup
 * around a native `<video>`.  A click clones it into the page and plays.  No player library.
 *
 * Every listener is delegated from `document`, so it works on cards that htmx, a jQuery
 * `.load()`, or this file insert later, on modern pages and on legacy Bootstrap pages alike.
 */

(function () {

    function getCsrfToken() {
        return window._cc && window._cc.getCsrfToken ? window._cc.getCsrfToken() : '';
    }

    function toastError(message) {
        if (window.SeeMe && SeeMe.toast) SeeMe.toast.error(message);
    }

    function megabytes(bytes) {
        return Math.round(bytes / (1024 * 1024));
    }

    // A 400 from the upload view is one short plain-text line meant for the user.  Anything else
    // (an HTML error page, nginx's 413 page) is not.
    function uploadErrorMessage(xhr, maxBytes) {
        if (xhr.status === 413) {
            return 'This video is too large to upload. The limit is ' + megabytes(maxBytes) + ' MB.';
        }
        if (xhr.status === 400) {
            var text = (xhr.responseText || '').trim();
            if (text && text.length <= 200 && text.charAt(0) !== '<') return text;
        }
        return 'Upload failed (' + (xhr.status || 'network error') + '). Try again.';
    }

    // "No videos yet" shows only while the grid has no card and no upload tile.  An inline
    // display, not `hidden`: on the legacy track `.empty-state`'s display beats `[hidden]`.
    function syncEmpty(group) {
        var empty = group && group.querySelector('[data-video-empty]');
        var grid = group && group.querySelector('[data-video-grid]');
        if (!empty || !grid) return;
        empty.style.display = grid.querySelector('[data-video-card], [data-video-uploading]') ? 'none' : '';
    }

    // Out-of-band count fragments (`_media_count_oob` in app/views/videos.py).  One response
    // carries the counts of every surface that can show the group (deal page, deal sheet,
    // property page), so only the fragments whose target is on this page are applied.  The
    // deal sheet and the property page share the `property-files-*` ids, so a target inside the
    // group's own card wins over one elsewhere on the page.
    function applyCountFragments(container, group) {
        var card = group && group.closest('.card');
        container.querySelectorAll('[hx-swap-oob]').forEach(function (oob) {
            if (!oob.id) return;
            var target = (card && card.querySelector('#' + CSS.escape(oob.id))) || document.getElementById(oob.id);
            if (target) target.replaceWith(oob);
        });
    }

    // ── Upload ──────────────────────────────────────────────────────────

    function uploadVideo(group, file) {
        var url = group.getAttribute('data-video-upload-url');
        var maxBytes = parseInt(group.getAttribute('data-video-max-bytes'), 10) || 0;
        var grid = group.querySelector('[data-video-grid]');
        var tileTemplate = group.querySelector('template[data-video-upload-tile]');
        if (!url || !grid || !tileTemplate) return;

        if (!file.type || file.type.indexOf('video/') !== 0) {
            toastError('"' + file.name + '" is not a video.');
            return;
        }
        if (maxBytes && file.size > maxBytes) {
            toastError('"' + file.name + '" is ' + megabytes(file.size) + ' MB. Videos must be '
                + megabytes(maxBytes) + ' MB or smaller.');
            return;
        }

        var tile = tileTemplate.content.firstElementChild.cloneNode(true);
        var status = tile.querySelector('[data-video-upload-status]');
        var bar = tile.querySelector('[data-video-upload-progress]');
        bar.style.width = '0%';
        grid.insertBefore(tile, grid.firstChild);
        syncEmpty(group);

        function fail(message) {
            tile.remove();
            syncEmpty(group);
            toastError(message);
        }

        var form = new FormData();
        form.append('file', file);
        var lead = group.getAttribute('data-video-acq-lead');
        var deal = group.getAttribute('data-video-acq-deal');
        if (lead) form.append('acq_lead', lead);
        if (deal) form.append('acq_deal', deal);

        var xhr = new XMLHttpRequest();
        xhr.open('POST', url);
        xhr.setRequestHeader('X-CSRFToken', getCsrfToken());
        // Lets HtmxAuthRedirectMiddleware answer an expired session with HX-Redirect.
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

        xhr.upload.onprogress = function (e) {
            if (!e.lengthComputable) return;
            var pct = Math.floor((e.loaded / e.total) * 100);
            bar.style.width = pct + '%';
            status.textContent = pct < 100 ? 'Uploading ' + pct + '%' : 'Saving…';
        };

        xhr.onload = function () {
            var redirect = xhr.getResponseHeader('HX-Redirect');
            if (redirect) {
                window.location.href = redirect;
                return;
            }
            if (xhr.status < 200 || xhr.status >= 300) {
                fail(uploadErrorMessage(xhr, maxBytes));
                return;
            }
            var holder = document.createElement('template');
            holder.innerHTML = xhr.responseText.trim();
            var card = holder.content.firstElementChild;
            if (!card || !card.matches('[data-video-card]')) {
                fail('Upload failed. Try again.');
                return;
            }
            tile.replaceWith(card);
            if (window.htmx) htmx.process(card);
            // Out-of-band fragments after the card (the Media photos + videos counts).
            applyCountFragments(holder.content, group);
            if (window.SeeMe && SeeMe.toast) SeeMe.toast.success('Video uploaded. It will play once processing finishes.');
        };

        xhr.onerror = function () {
            fail('Upload failed (network error). Try again.');
        };

        xhr.send(form);
    }

    document.addEventListener('click', function (e) {
        var button = e.target.closest && e.target.closest('[data-video-upload-button]');
        if (!button) return;
        var group = button.closest('[data-video-group]');
        var input = group && group.querySelector('[data-video-input]');
        if (input) input.click();
    });

    document.addEventListener('change', function (e) {
        if (!e.target.matches || !e.target.matches('[data-video-input]')) return;
        var input = e.target;
        var group = input.closest('[data-video-group]');
        var file = input.files && input.files[0];
        input.value = '';
        if (group && file) uploadVideo(group, file);
    });

    // Card swaps.  A processing card's poll that answers 404 (the video was deleted, its owner
    // was deleted, or access ended) would otherwise repeat every few seconds forever: htmx
    // swaps nothing on an error and keeps the trigger.  Removing the card stops it, and
    // clearing `isError` keeps htmx from logging the expected 404 as a console error.
    // A delete swaps its card out (`hx-swap="delete"`); the deleting button leaves the page
    // with the card, so the events after the swap never bubble here.  Both cases catch the
    // group before the swap and recheck "No videos yet" once the card is gone.  A delete's
    // count fragments are applied here and dropped from the response, because htmx would log
    // an error for each one whose target is not on this page.
    document.addEventListener('htmx:beforeSwap', function (e) {
        var target = e.detail && e.detail.target;
        if (!target || !target.matches || !target.matches('[data-video-card]')) return;
        var group = target.closest('[data-video-group]');
        if (target.matches('[hx-get]') && e.detail.xhr && e.detail.xhr.status === 404) {
            e.detail.shouldSwap = false;
            e.detail.isError = false;
            target.remove();
            syncEmpty(group);
            return;
        }
        if (e.detail.requestConfig && e.detail.requestConfig.verb === 'delete' && e.detail.serverResponse) {
            var holder = document.createElement('template');
            if (e.detail.xhr && e.detail.xhr.status >= 200 && e.detail.xhr.status < 300) {
                holder.innerHTML = e.detail.serverResponse;
                applyCountFragments(holder.content, group);
            }
            e.detail.serverResponse = '';
        }
        if (group) setTimeout(function () { syncEmpty(group); }, 0);
    });

    // A delete carries the group's lead or deal, so the response's Media count is this pane's
    // (`_delete_pane_pursuit` in app/views/videos.py), not the video's own pursuit's.
    document.addEventListener('htmx:configRequest', function (e) {
        var elt = e.detail && e.detail.elt;
        if (!elt || !elt.matches || !elt.matches('[data-video-delete]')) return;
        var group = elt.closest('[data-video-group]');
        if (!group) return;
        var lead = group.getAttribute('data-video-acq-lead');
        var deal = group.getAttribute('data-video-acq-deal');
        if (lead) e.detail.parameters['acq_lead'] = lead;
        if (deal) e.detail.parameters['acq_deal'] = deal;
    });

    // ── Player ──────────────────────────────────────────────────────────

    var player = null;
    var savedOverflow = '';

    function closePlayer() {
        if (!player) return;
        var video = player.querySelector('video');
        if (video) video.pause();
        player.remove();
        player = null;
        document.body.style.overflow = savedOverflow;
    }

    function openPlayer(card) {
        var template = card.querySelector('template[data-video-player]');
        if (!template) return false;
        closePlayer();
        player = template.content.firstElementChild.cloneNode(true);
        // Inside a legacy Bootstrap modal, Bootstrap pulls focus back into the modal whenever
        // something outside it is focused, so the player goes inside the modal there.  Anywhere
        // else (pages, sheets) it goes on <body>, where position: fixed covers the viewport.
        var bootstrapModal = card.closest('.modal-content') ? card.closest('.modal') : null;
        (bootstrapModal || document.body).appendChild(player);
        savedOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        player.offsetHeight; // reflow so the fade-in transition runs
        player.classList.add('lightbox--open');
        var close = player.querySelector('[data-video-player-close]');
        if (close) close.focus();
        var video = player.querySelector('video');
        if (video) {
            var playing = video.play();
            if (playing && playing.catch) playing.catch(function () { /* the user presses play */ });
        }
        return true;
    }

    document.addEventListener('click', function (e) {
        if (!e.target.closest) return;
        var play = e.target.closest('[data-video-play]');
        if (play) {
            var card = play.closest('[data-video-card]');
            if (card && openPlayer(card)) e.preventDefault();
            return;
        }
        if (!player) return;
        if (e.target.closest('[data-video-player-close]') || e.target.matches('[data-video-player-backdrop]')) {
            closePlayer();
        }
    });

    // Capture on window so Escape closes only the player, not the sheet or Bootstrap modal under it.
    window.addEventListener('keydown', function (e) {
        if (!player || e.key !== 'Escape') return;
        e.preventDefault();
        e.stopPropagation();
        closePlayer();
    }, true);

})();
